<!-- ============ 1 · HERO ============ -->
<section class="relative isolate overflow-hidden bg-white">
  <div class="relative z-10 mx-auto max-w-6xl px-6 pb-24 pt-16 sm:pt-24">
    <div class="mx-auto max-w-3xl text-center">
      <span
        class="mb-4 inline-flex items-center gap-2 rounded-full border border-[#B7CEFE] bg-white/80 px-4 py-1.5 text-sm font-semibold text-[#1846B4] shadow-sm"
      >
        <svg
          class="h-4 w-4 text-[#F4C400]"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z" />
        </svg>
        Introducing Principle AI Receptionist
      </span>

      <h1
        class="mt-6 text-5xl font-extrabold leading-[1.03] tracking-tight text-[#0E1D3A] sm:text-6xl"
      >
        More calls answered.<br />
        <span class="text-[#2B6EF7]">More appointments booked.</span>
      </h1>

      <p class="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-[#6B7488]">
        Principle AI Receptionist answers patient calls day or night, manages
        appointments and responds to common questions using real information
        from your practice. Your front desk stays in control, while your team
        gives their full attention to the patients who need them.
      </p>

      @if (isBooked()) {
        <!-- DEMO BOOKED STATE -->
        <div class="mt-9">
          <div
            class="mx-auto max-w-md rounded-2xl border border-[#B7CEFE] bg-white p-5 shadow-lg"
          >
            <div class="flex items-center justify-center gap-3">
              <span
                class="flex h-10 w-10 items-center justify-center rounded-full bg-[#2B6EF7] text-white"
              >
                <svg
                  class="h-6 w-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  aria-hidden="true"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </span>
              <div class="text-left">
                <p class="text-base font-bold text-[#0E1D3A]">
                  Demo booked — we'll be in touch!
                </p>
                <p class="text-sm text-[#6B7488]">
                  Check your inbox for the calendar invite.
                </p>
              </div>
            </div>
            <button
              type="button"
              class="mt-4 text-sm font-semibold text-[#1B57DB] underline underline-offset-2 hover:text-[#1846B4]"
              (click)="openBookingDialog()"
            >
              Book another time
            </button>
          </div>
        </div>
      } @else {
        <!-- DEFAULT CTA STATE -->
        <div
          class="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row"
        >
          <button
            type="button"
            class="group inline-flex items-center gap-2 rounded-full bg-[#2B6EF7] px-7 py-3.5 text-base font-semibold text-white shadow-[0_14px_30px_rgba(43,110,247,0.35)] transition hover:bg-[#1B57DB]"
            (click)="openBookingDialog()"
          >
            Get Started
            <svg
              class="h-5 w-5 transition group-hover:translate-x-0.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      }
    </div>

    <!-- Hero visual: mock live-call card, framed by brand quarter-circle arcs -->
    <div class="relative mx-auto mt-20 max-w-3xl">
      <div
        class="pointer-events-none absolute -bottom-10 -left-10 z-0 h-52 w-52 rounded-tr-full bg-[#2B6EF7]"
        aria-hidden="true"
      ></div>
      <div
        class="pointer-events-none absolute -right-8 -top-8 z-0 h-32 w-32 rounded-bl-full bg-[#F4C400]"
        aria-hidden="true"
      ></div>

      <div
        class="relative z-10 rounded-[2rem] border border-slate-200/70 bg-white p-2 shadow-xl"
      >
        <div
          class="rounded-[26px] bg-gradient-to-b from-[#EDF3FF]/60 to-white p-6 sm:p-8"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <span
                class="flex h-11 w-11 items-center justify-center rounded-full bg-[#2B6EF7] text-white"
              >
                <svg
                  class="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  aria-hidden="true"
                >
                  <path
                    d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.98.36 1.93.7 2.85a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.92.34 1.87.57 2.85.7A2 2 0 0 1 22 16.92Z"
                  />
                </svg>
              </span>
              <div>
                <p class="text-sm font-semibold text-[#0E1D3A]">
                  Incoming call · Smile Dental
                </p>
                <p class="text-xs text-[#6B7488]">
                  AI Receptionist answered in 0.8s
                </p>
              </div>
            </div>
            <span
              class="inline-flex items-center gap-1.5 rounded-full bg-[#EDF3FF] px-2.5 py-1 text-xs font-semibold text-[#1846B4]"
            >
              <span
                class="pulse-dot h-1.5 w-1.5 rounded-full bg-[#2B6EF7]"
              ></span>
              Live
            </span>
          </div>
          <div class="mt-5 space-y-3">
            <div
              class="max-w-[80%] rounded-2xl rounded-tl-sm bg-slate-100 px-4 py-2.5 text-sm text-[#3C4763]"
            >
              "Hi, I'd like to book a check-up for next week."
            </div>
            <div
              class="ml-auto max-w-[80%] rounded-2xl rounded-tr-sm bg-[#2B6EF7] px-4 py-2.5 text-sm text-white"
            >
              "Of course! I have Tuesday 2:00pm or Thursday 10:30am with Dr Lee
              — which suits you?"
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ============ 2 · FEATURES ============ -->
<section class="bg-[#FBFAF3]">
  <div class="mx-auto max-w-6xl px-6 py-20 sm:py-24">
    <div class="mx-auto max-w-2xl text-center">
      <span class="text-xs font-bold uppercase tracking-[0.18em] text-[#2B6EF7]"
        >What it does</span
      >
      <h2
        class="!mt-4 text-3xl font-bold leading-6 tracking-tight text-[#0E1D3A] sm:text-4xl sm:leading-7"
      >
        Ready to help whenever patients call
      </h2>
      <p class="mt-4 text-lg text-[#6B7488]">
        Built into Principle and tailored to your practice, AI Receptionist
        helps you capture more opportunities while reducing pressure on your
        front desk.
      </p>
    </div>

    <div class="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      <!-- Card 1 -->
      <div
        class="group relative overflow-hidden rounded-2xl border border-slate-100 bg-white p-6 shadow-lg transition duration-300 hover:-translate-y-1 hover:shadow-2xl"
      >
        <span
          class="bg-[#F4C400]/15 absolute -right-6 -top-6 h-16 w-16 rounded-bl-full transition group-hover:bg-[#F4C400]/25"
        ></span>
        <span
          class="relative flex h-12 w-12 items-center justify-center rounded-xl bg-[#2B6EF7] text-white shadow-sm"
        >
          <svg
            class="h-6 w-6"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
        </span>
        <h3 class="relative mt-5 text-lg font-bold text-[#0E1D3A]">
          Answers calls day or night
        </h3>
        <p class="relative mt-2 text-sm leading-relaxed text-[#6B7488]">
          Patients receive a prompt, helpful response, even after hours or when
          your team is busy.
        </p>
      </div>
      <!-- Card 2 -->
      <div
        class="group relative overflow-hidden rounded-2xl border border-slate-100 bg-white p-6 shadow-lg transition duration-300 hover:-translate-y-1 hover:shadow-2xl"
      >
        <span
          class="bg-[#F4C400]/15 absolute -right-6 -top-6 h-16 w-16 rounded-bl-full transition group-hover:bg-[#F4C400]/25"
        ></span>
        <span
          class="relative flex h-12 w-12 items-center justify-center rounded-xl bg-[#2B6EF7] text-white shadow-sm"
        >
          <svg
            class="h-6 w-6"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18M9 16l2 2 4-4" />
          </svg>
        </span>
        <h3 class="relative mt-5 text-lg font-bold text-[#0E1D3A]">
          Manages appointments
        </h3>
        <p class="relative mt-2 text-sm leading-relaxed text-[#6B7488]">
          Books, reschedules and cancels appointments using your live
          availability in Principle.
        </p>
      </div>
      <!-- Card 3 -->
      <div
        class="group relative overflow-hidden rounded-2xl border border-slate-100 bg-white p-6 shadow-lg transition duration-300 hover:-translate-y-1 hover:shadow-2xl"
      >
        <span
          class="bg-[#F4C400]/15 absolute -right-6 -top-6 h-16 w-16 rounded-bl-full transition group-hover:bg-[#F4C400]/25"
        ></span>
        <span
          class="relative flex h-12 w-12 items-center justify-center rounded-xl bg-[#2B6EF7] text-white shadow-sm"
        >
          <svg
            class="h-6 w-6"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path
              d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
            />
          </svg>
        </span>
        <h3 class="relative mt-5 text-lg font-bold text-[#0E1D3A]">
          Answers patient questions
        </h3>
        <p class="relative mt-2 text-sm leading-relaxed text-[#6B7488]">
          Provides helpful answers about your services, providers, pricing and
          practice policies.
        </p>
      </div>
      <!-- Card 4 -->
      <div
        class="group relative overflow-hidden rounded-2xl border border-slate-100 bg-white p-6 shadow-lg transition duration-300 hover:-translate-y-1 hover:shadow-2xl"
      >
        <span
          class="bg-[#F4C400]/15 absolute -right-6 -top-6 h-16 w-16 rounded-bl-full transition group-hover:bg-[#F4C400]/25"
        ></span>
        <span
          class="relative flex h-12 w-12 items-center justify-center rounded-xl bg-[#2B6EF7] text-white shadow-sm"
        >
          <svg
            class="h-6 w-6"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path
              d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5a9 9 0 0 1 18 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3"
            />
          </svg>
        </span>
        <h3 class="relative mt-5 text-lg font-bold text-[#0E1D3A]">
          Supports your front desk
        </h3>
        <p class="relative mt-2 text-sm leading-relaxed text-[#6B7488]">
          Handles routine calls so your team can spend more time supporting
          patients in the practice.
        </p>
      </div>
    </div>
  </div>
</section>

<!-- ============ 3 · CTA (bottom) ============ -->
<section class="mx-auto max-w-6xl px-6 py-20">
  <div
    class="relative isolate overflow-hidden rounded-[2rem] bg-[#0E1D3A] px-8 py-16 text-center shadow-[0_14px_30px_rgba(43,110,247,0.35)] sm:px-12 sm:py-20"
  >
    <!-- brand arcs bleeding off the dark band -->
    <div class="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
      <div
        class="absolute -left-16 -top-16 h-64 w-64 rounded-br-full bg-[#2B6EF7]"
      ></div>
      <div
        class="absolute -bottom-20 -right-20 h-72 w-72 rounded-tl-full bg-[#F4C400]"
      ></div>
    </div>

    <div class="relative z-10">
      @if (isBooked()) {
        <span
          class="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#2B6EF7] text-white shadow-lg"
        >
          <svg
            class="h-7 w-7"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            aria-hidden="true"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </span>
        <h2
          class="mx-auto !mt-6 max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl"
        >
          Demo booked — we'll be in touch!
        </h2>
        <p class="mx-auto mt-4 max-w-xl text-lg text-[#DBE7FF]">
          Check your inbox for the calendar invite.
        </p>
        <button
          type="button"
          class="mt-8 text-base font-semibold text-white underline underline-offset-4 transition hover:text-[#DBE7FF]"
          (click)="openBookingDialog()"
        >
          Book another time
        </button>
      } @else {
        <span
          class="text-xs font-bold uppercase tracking-[0.18em] text-[#F4C400]"
        >
          Get Started
        </span>
        <h2
          class="mx-auto !mt-4 max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl"
        >
          Turn more patient calls into appointments
        </h2>
        <p class="mx-auto mt-4 max-w-xl text-lg text-[#DBE7FF]">
          Discover how Principle AI Receptionist can help reduce missed
          opportunities and ease the pressure on your team.
        </p>
        <button
          type="button"
          class="group mt-9 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-semibold text-[#1846B4] shadow-lg transition hover:bg-[#EDF3FF]"
          (click)="openBookingDialog()"
        >
          Get Started
          <svg
            class="h-5 w-5 transition group-hover:translate-x-0.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <path d="M5 12h14M13 5l7 7-7 7" />
          </svg>
        </button>
      }
    </div>
  </div>
</section>
